import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { 
  ArrowLeftIcon, 
  UserIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ChevronRightIcon,
  HomeIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'
import PoolAdminRoute from '../components/PoolAdminRoute'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface PoolPicksData {
  poolId: string
  poolName: string
  poolSettings: {
    track: string
    raceDate: string
    pickDeadline: string
  }
  totalParticipants: number
  totalRaces: number
  participants: ParticipantPicks[]
  races: Race[]
}

interface ParticipantPicks {
  userId: string
  name: string
  email: string
  joinedAt: string
  totalPicks: number
  completedPicks: number
  picks: HorsePick[]
}

interface HorsePick {
  raceNumber: number
  horseId: string
  horseName: string
  postPosition: string
  postPositionColor?: {
    background_color: string
    text_color: string
    name: string
  }
  jockey: string
  trainer: string
  isValid: boolean
  isScratched: boolean
  pickedAt: string
}

interface Race {
  raceNumber: number
  postTime: string
  track: string
  raceType: string
  conditions: string
  purse: string
  distance?: string
  entries: Horse[]
  scratches?: string[]
  status: 'upcoming' | 'running' | 'final'
  results?: {
    winner?: string
    place?: string
    show?: string
  }
}

interface Horse {
  horse_id: string
  post_position: string
  post_position_color?: {
    background_color: string
    text_color: string
    name: string
  }
  horse_name: string
  jockey: string
  trainer: string
  weight: string
  medication?: string
  age_sex: string
}

export default function HorseRacingPoolPicksPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const { user, getAccessTokenSilently } = useAuth0()
  const [picksData, setPicksData] = useState<PoolPicksData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedRace, setSelectedRace] = useState<number | null>(null)
  const [showOnlyIncomplete, setShowOnlyIncomplete] = useState(false)

  useEffect(() => {
    if (poolId) {
      fetchPicks()
    }
  }, [poolId])

  const fetchPicks = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}/horse-picks/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch picks: ${response.statusText}`)
      }

      const data = await response.json()
      setPicksData(data)
    } catch (err) {
      console.error('Error fetching picks:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch picks')
    } finally {
      setLoading(false)
    }
  }

  const exportPicks = async () => {
    if (!picksData) return
    
    try {
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}/horse-picks/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to export picks: ${response.statusText}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = `${picksData.poolName.replace(/\s+/g, '_')}_picks.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error exporting picks:', err)
    }
  }

  const getPickForRace = (participant: ParticipantPicks, raceNumber: number): HorsePick | null => {
    return participant.picks.find(pick => pick.raceNumber === raceNumber) || null
  }

  const getCompletionRate = (participant: ParticipantPicks): number => {
    if (!picksData) return 0
    return Math.round((participant.completedPicks / picksData.totalRaces) * 100)
  }

  const filteredParticipants = showOnlyIncomplete 
    ? picksData?.participants.filter(p => p.completedPicks < (picksData?.totalRaces || 0)) || []
    : picksData?.participants || []

  const isDeadlinePassed = picksData ? new Date() > new Date(picksData.poolSettings.pickDeadline) : false

  if (loading) {
    return (
      <PoolAdminRoute>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </PoolAdminRoute>
    )
  }

  if (error) {
    return (
      <PoolAdminRoute>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
          </div>
        </div>
      </PoolAdminRoute>
    )
  }

  if (!picksData) {
    return (
      <PoolAdminRoute>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <UserIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No picks data available</h2>
            <p className="text-gray-600 dark:text-gray-300">Participant picks will appear here once they start making selections.</p>
          </div>
        </div>
      </PoolAdminRoute>
    )
  }

  return (
    <PoolAdminRoute>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link
                to="/pools"
                className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white"
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                Pools
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <Link
                  to="/admin/pools/horse-racing"
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2 dark:text-gray-400 dark:hover:text-white"
                >
                  Horse Racing Pools
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <Link
                  to={`/admin/pools/horse-racing/${poolId}`}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2 dark:text-gray-400 dark:hover:text-white"
                >
                  {picksData.poolName}
                </Link>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2 dark:text-gray-400">
                  Picks
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {picksData.poolName} - Participant Picks
              </h1>
              <p className="mt-1 text-lg text-gray-600 dark:text-gray-300">
                {picksData.poolSettings.track} • {picksData.poolSettings.raceDate}
              </p>
              <div className="mt-2 flex items-center space-x-4">
                <div className={`flex items-center text-sm ${
                  isDeadlinePassed 
                    ? 'text-red-600 dark:text-red-400' 
                    : 'text-green-600 dark:text-green-400'
                }`}>
                  {isDeadlinePassed ? (
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                  ) : (
                    <ClockIcon className="w-4 h-4 mr-1" />
                  )}
                  Pick Deadline: {new Date(picksData.poolSettings.pickDeadline).toLocaleString()}
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={exportPicks}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                Export CSV
              </button>
              <Link
                to={`/admin/pools/horse-racing/${poolId}`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back to Pool
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <UserIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Participants</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{picksData.totalParticipants}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <span className="text-2xl mr-4">🐎</span>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Races</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{picksData.totalRaces}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <CheckCircleIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Complete Picks</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {picksData.participants.filter(p => p.completedPicks === picksData.totalRaces).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Incomplete Picks</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {picksData.participants.filter(p => p.completedPicks < picksData.totalRaces).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showOnlyIncomplete}
                onChange={(e) => setShowOnlyIncomplete(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Show only incomplete picks
              </span>
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-700 dark:text-gray-300">Filter by race:</label>
            <select
              value={selectedRace || ''}
              onChange={(e) => setSelectedRace(e.target.value ? parseInt(e.target.value) : null)}
              className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            >
              <option value="">All Races</option>
              {picksData.races.map((race) => (
                <option key={race.raceNumber} value={race.raceNumber}>
                  Race {race.raceNumber} - {race.raceType}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Picks Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Participant Picks</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Participant
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Completion
                  </th>
                  {(selectedRace ? [selectedRace] : picksData.races.map(r => r.raceNumber)).map((raceNum) => (
                    <th key={raceNum} className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Race {raceNum}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredParticipants.map((participant) => (
                  <tr key={participant.userId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {participant.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{participant.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center">
                        <div className={`text-sm font-medium ${
                          participant.completedPicks === picksData.totalRaces
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-yellow-600 dark:text-yellow-400'
                        }`}>
                          {participant.completedPicks}/{picksData.totalRaces}
                        </div>
                        <div className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                          ({getCompletionRate(participant)}%)
                        </div>
                      </div>
                    </td>
                    {(selectedRace ? [selectedRace] : picksData.races.map(r => r.raceNumber)).map((raceNum) => {
                      const pick = getPickForRace(participant, raceNum)
                      return (
                        <td key={raceNum} className="px-4 py-4 whitespace-nowrap text-center">
                          {pick ? (
                            <div className="text-xs">
                              <div className="flex items-center justify-center space-x-1 mb-1">
                                {pick.postPositionColor ? (
                                  <div
                                    className="w-3 h-3 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-xs font-bold"
                                    style={{
                                      backgroundColor: pick.postPositionColor.background_color,
                                      color: pick.postPositionColor.text_color
                                    }}
                                    title={pick.postPositionColor.name}
                                  >
                                    {pick.postPosition}
                                  </div>
                                ) : (
                                  <div className="w-3 h-3 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-bold">
                                    {pick.postPosition}
                                  </div>
                                )}
                              </div>
                              <div className={`font-medium ${
                                pick.isScratched 
                                  ? 'text-red-600 dark:text-red-400 line-through' 
                                  : 'text-gray-900 dark:text-white'
                              }`}>
                                {pick.horseName}
                              </div>
                              {pick.isScratched && (
                                <div className="text-red-600 dark:text-red-400 text-xs">
                                  SCRATCHED
                                </div>
                              )}
                              <div className="text-gray-500 dark:text-gray-400">
                                <div>J: {pick.jockey}</div>
                                <div>T: {pick.trainer}</div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">No pick</span>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredParticipants.length === 0 && (
          <div className="text-center py-8">
            <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {showOnlyIncomplete ? 'All participants have completed their picks!' : 'No participants found.'}
            </p>
          </div>
        )}
      </div>
    </PoolAdminRoute>
  )
}
